import React, { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import {
   Save as SaveIcon,
   Download as DownloadIcon,
   Share as ShareIcon,
   Refresh as ReEnhanceIcon,
   AddPhotoAlternate as NewPhotoIcon,
} from '@mui/icons-material';

interface StepSaveProps {
   originalUrl: string;
   enhancedUrl: string;
   onSave: () => Promise<void>;
   onReEnhance: () => void;
   onNewPhoto: () => void;
}

const StepSave: React.FC<StepSaveProps> = ({
   originalUrl,
   enhancedUrl,
   onSave,
   onReEnhance,
   onNewPhoto,
}) => {
   const [snackMsg, setSnackMsg] = useState('');
   const [snackSeverity, setSnackSeverity] = useState<'success' | 'error'>('success');
   const [snackOpen, setSnackOpen] = useState(false);
   const [isSaving, setIsSaving] = useState(false);

   const handleSave = async () => {
      try {
         setIsSaving(true);
         await onSave();
         setSnackSeverity('success');
         setSnackMsg('Photo saved successfully');
      } catch {
         setSnackSeverity('error');
         setSnackMsg('Failed to save photo');
      } finally {
         setIsSaving(false);
         setSnackOpen(true);
      }
   };

   const handleDownload = async () => {
      try {
         const proxyUrl = `${process.env.REACT_APP_API_URL}/api/photos/download-proxy?url=${encodeURIComponent(enhancedUrl)}`;
         const { getAuthHeaders } = await import('../../../../utils/auth');
         const headers = await getAuthHeaders();
         const response = await fetch(proxyUrl, {
            headers: { Authorization: headers.Authorization },
         });
         if (!response.ok) throw new Error('Download failed');
         const blob = await response.blob();
         const blobUrl = window.URL.createObjectURL(blob);
         const a = document.createElement('a');
         a.href = blobUrl;
         a.download = `enhanced_${Date.now()}.jpg`;
         document.body.appendChild(a);
         a.click();
         document.body.removeChild(a);
         window.URL.revokeObjectURL(blobUrl);
      } catch {
         setSnackSeverity('error');
         setSnackMsg('Failed to download photo');
         setSnackOpen(true);
      }
   };

   const handleShare = async () => {
      try {
         await navigator.clipboard.writeText(enhancedUrl);
         setSnackSeverity('success');
         setSnackMsg('Link copied to clipboard');
         setSnackOpen(true);
      } catch {
         setSnackSeverity('error');
         setSnackMsg('Failed to copy link');
         setSnackOpen(true);
      }
   };

   return (
      <Box>
         {/* Before / After */}
         <Box sx={{
            display: 'flex',
            gap: 1.5,
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' },
         }}>
            <Box sx={{ flex: 1 }}>
               <Typography sx={{ fontSize: '11px', color: '#546E7A', mb: 0.5, textAlign: 'center', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Original
               </Typography>
               <Box sx={{
                  height: { xs: 200, sm: 260 },
                  borderRadius: '8px',
                  border: '1px solid #253530',
                  overflow: 'hidden',
                  background: '#0A0F0C',
               }}>
                  <img
                     src={originalUrl}
                     alt="Original"
                     style={{ width: '100%', height: '100%', objectFit: 'contain', imageOrientation: 'from-image' }}
                  />
               </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
               <Typography sx={{ fontSize: '11px', color: '#1D9E75', mb: 0.5, textAlign: 'center', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Enhanced
               </Typography>
               <Box sx={{
                  height: { xs: 200, sm: 260 },
                  borderRadius: '8px',
                  border: '1px solid #1D9E75',
                  overflow: 'hidden',
                  background: '#0A0F0C',
               }}>
                  <img
                     src={enhancedUrl}
                     alt="Enhanced"
                     style={{ width: '100%', height: '100%', objectFit: 'contain', imageOrientation: 'from-image' }}
                  />
               </Box>
            </Box>
         </Box>

         {/* Action buttons */}
         <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
               fullWidth
               startIcon={<SaveIcon />}
               onClick={handleSave}
               disabled={isSaving}
               sx={{
                  background: '#1D9E75', color: '#FFFFFF', borderRadius: '8px',
                  py: 1.2, fontSize: '13px', fontWeight: 600, textTransform: 'none',
                  '&:hover': { background: '#178a65' },
                  '&.Mui-disabled': { background: '#253530', color: '#546E7A' },
               }}
            >
               {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
               fullWidth
               startIcon={<DownloadIcon />}
               onClick={handleDownload}
               sx={{
                  background: '#1A2420', color: '#FFFFFF', borderRadius: '8px',
                  border: '1px solid #1D9E75', py: 1.2, fontSize: '13px', textTransform: 'none',
                  '&:hover': { background: '#253530' },
               }}
            >
               Download
            </Button>
            <Button
               fullWidth
               startIcon={<ShareIcon />}
               onClick={handleShare}
               sx={{
                  background: '#1A2420', color: '#C9A84C', borderRadius: '8px',
                  border: '1px solid rgba(201,168,76,0.4)', py: 1.2, fontSize: '13px', textTransform: 'none',
                  '&:hover': { background: '#253530' },
               }}
            >
               Share
            </Button>
         </Box>

         {/* Navigation */}
         <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
               fullWidth
               startIcon={<ReEnhanceIcon />}
               onClick={onReEnhance}
               variant="outlined"
               sx={{
                  borderColor: '#253530', color: '#B0BEC5', borderRadius: '8px',
                  py: 1, fontSize: '13px', textTransform: 'none',
                  '&:hover': { borderColor: '#1D9E75', color: '#FFFFFF' },
               }}
            >
               Re-enhance
            </Button>
            <Button
               fullWidth
               startIcon={<NewPhotoIcon />}
               onClick={onNewPhoto}
               variant="outlined"
               sx={{
                  borderColor: '#1D9E75', color: '#1D9E75', borderRadius: '8px',
                  py: 1, fontSize: '13px', textTransform: 'none',
                  '&:hover': { borderColor: '#1D9E75', background: 'rgba(29,158,117,0.08)' },
               }}
            >
               Enhance another
            </Button>
         </Box>

         <Snackbar
            open={snackOpen}
            autoHideDuration={3000}
            onClose={() => setSnackOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         >
            <Alert onClose={() => setSnackOpen(false)} severity={snackSeverity}>
               {snackMsg}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default StepSave;
